import { push } from "react-router-redux";
import * as i18nUtils from "util/i18n";
import { actions as transferActions } from "reducers/transfer";
import { actions as servicePaymentsActions } from "reducers/servicePayments";
import { getV3PrivateNavAccountsPath } from "util/v3UxConfig";
import { actions as checkActions } from "reducers/checks";
import { getServicePaymentsPath, isServicePaymentsPath } from "util/servicePaymentsRoutes";
import getTransferPath from "./transfers";

export const maskedAccountNumber = (accountNumber) => {
    const amountOfVisibleNumbers = 4;
    const minAmountOfMaskedNumbers = 4;
    const visibleNumbers = accountNumber?.substring(accountNumber?.length - amountOfVisibleNumbers);
    const amountOfMaskedNumbers = accountNumber?.length - amountOfVisibleNumbers;
    const amountOfAsteriskMask =
        amountOfMaskedNumbers >= minAmountOfMaskedNumbers ? amountOfMaskedNumbers : minAmountOfMaskedNumbers;
    const mask = Array(amountOfAsteriskMask + 1).join("*");
    return mask + visibleNumbers;
};

export const maskedAccountNumberLimited = (accountNumber, minAmountOfMaskedNumbers = 4) => {
    const amountOfVisibleNumbers = 4;
    const visibleNumbers = accountNumber?.substring(accountNumber?.length - amountOfVisibleNumbers);
    const mask = Array(minAmountOfMaskedNumbers + 1).join("*");
    return mask + visibleNumbers;
};

export const productTypes = { CA: "savings", CC: "checking" };

export const getAccountNumber = (cbu) => cbu.slice(8, 21);

export const getIncomingDebinAccountName = (account) => {
    if (!account) {
        return "";
    }

    const { productAlias, number: accountNumber, productType, currency } = account;
    return `${productAlias || ""} ${maskedAccountNumber(accountNumber)} ${productType} ${i18nUtils.get(
        `currency.label.${currency}`,
    )}`;
};

export const getUserAccount = (accounts, cbu) => accounts.find((account) => cbu === account?.cbu);

export function accountContextMenuItems(
    dispatch,
    activeEnvironment,
    debitAccount,
    arrayItemsDropdown = [],
    currentPathname = null,
) {
    let items = [];
    const hasPermission = activeEnvironment.permissions.transferAccess;

    if (activeEnvironment && arrayItemsDropdown.length > 0) {
        items = [...items, ...arrayItemsDropdown];
    }

    if (activeEnvironment && activeEnvironment.permissions.transferInternal && hasPermission) {
        items = [
            ...items,
            {
                label: "accounts.actions.transfer.internal",
                onClick: () => {
                    dispatch(
                        transferActions.loadTransfer({
                            debitAccount: { value: debitAccount },
                        }),
                    );
                    dispatch(push(getTransferPath()));
                },
                icon: "CashflowIn",
            },
        ];
    }

    if (activeEnvironment && activeEnvironment.permissions.transferThirdParties && hasPermission) {
        items = [
            ...items,
            {
                label: "accounts.actions.transfer.external",
                onClick: () => {
                    dispatch(
                        transferActions.loadExternalTransfer({
                            debitAccount: { value: debitAccount },
                        }),
                    );
                    dispatch(push(getTransferPath("thirdParties")));
                },
                icon: "TransferAlt",
            },
        ];
    }

    if (activeEnvironment && activeEnvironment.permissions["pay.afip"]) {
        items = [
            ...items,
            {
                label: "accounts.actions.payment.services",
                onClick: () => {
                    const landing = getServicePaymentsPath();
                    if (currentPathname && !isServicePaymentsPath(currentPathname)) {
                        dispatch(servicePaymentsActions.setModuleEntryPath(currentPathname));
                    }
                    dispatch(servicePaymentsActions.setDefaultAccount(debitAccount));
                    dispatch(push(landing));
                },
                icon: "PayService",
            },
        ];
    }

    if (activeEnvironment && activeEnvironment?.permissions.requestCheckbook) {
        items = [
            ...items,
            {
                label: "accounts.actions.checkbook",
                onClick: () => {
                    dispatch(checkActions.setDefaultAccount(debitAccount));
                    const returnPath =
                        currentPathname && currentPathname.startsWith("/")
                            ? currentPathname
                            : getV3PrivateNavAccountsPath();
                    dispatch(
                        push({
                            pathname: "/checks",
                            state: { return: returnPath },
                        }),
                    );
                },
                icon: "AddCheq",
            },
        ];
    }

    return items;
}

export function getAccountDescription({ number, productType, currency }) {
    return `${maskedAccountNumber(number)} ${productType} ${i18nUtils.get(`currency.label.${currency}`)}`;
}

export function getAccountDescriptionLimited({ number, productType, currency, mask = 4 }) {
    return `${maskedAccountNumberLimited(number, mask)} ${productType} ${i18nUtils.get(`currency.label.${currency}`)}`;
}

/**
 * Cuenta débito para tickets / comprobantes: siempre 4 asteriscos cuando hay número, tipo y moneda
 * (alineado con emisión masiva e-CHEQ y firmas múltiples). Si faltan datos, conserva shortLabel del backend.
 *
 * @param {object|null|undefined} accountInfo objeto cuenta (p. ej. respuesta accounts.info)
 * @returns {string}
 */
export function getDebitAccountTicketLabel(accountInfo) {
    if (!accountInfo) {
        return "";
    }
    const { number, productType, currency, shortLabel } = accountInfo;
    if (number != null && String(number).length > 0 && productType && currency != null && String(currency).length > 0) {
        return getAccountDescriptionLimited({ number, productType, currency });
    }
    return shortLabel || "";
}

export function getRutPathAccount() {
    return getV3PrivateNavAccountsPath();
}

export function getAccountName(account) {
    if (!account) {
        return "";
    }
    const { productAlias, number: accountNumber, productType, currency } = account;
    return `${productAlias || ""} ${maskedAccountNumber(accountNumber)} ${productType} ${i18nUtils.get(
        `currency.label.${currency}`,
    )}`;
}
